import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Building2, Users, LogOut, Settings } from "lucide-react";
import { ADMIN_TENANTS_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const AdminDashboard = () => {
    const navigate  = useNavigate();
    const { fetch } = useApiMethods();
    const [tenants, setTenants]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [search,  setSearch]    = useState("");

    const adminName = sessionStorage.getItem("admin_name") || "Admin";

    const loadTenants = async () => {
        setLoading(true);
        const res = await fetch(ADMIN_TENANTS_URL, {}, setLoading, true);
        if (res?.status === "success") setTenants(res.data || []);
        else ToastMsg("error", "Failed to load clients");
        setLoading(false);
    };

    useEffect(() => { loadTenants(); }, []);

    const handleLogout = () => {
        sessionStorage.removeItem("admin_token");
        sessionStorage.removeItem("admin_name");
        navigate("/admin/login");
    };

    const filtered = tenants.filter(t =>
        !search || t.org_name.toLowerCase().includes(search.toLowerCase())
    );

    const active   = tenants.filter(t => t.is_active).length;
    const inactive = tenants.filter(t => !t.is_active).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Top bar */}
            <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">Gro Wing CRM Onboarding</span>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate("/admin/plans")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700 rounded-lg transition-colors">
                        <Settings className="w-4 h-4" /> Plans
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Welcome, {adminName}</span>
                    <button onClick={handleLogout}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                        { label: "Total Clients",    value: tenants.length, color: "bg-blue-500" },
                        { label: "Active",           value: active,         color: "bg-green-500" },
                        { label: "Inactive",         value: inactive,       color: "bg-red-500" },
                    ].map(s => (
                        <div key={s.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex items-center gap-4">
                            <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{s.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Toolbar */}
                <div className="flex items-center gap-3 mb-4">
                    <input
                        type="text" placeholder="Search clients…" value={search} onChange={e => setSearch(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 w-64"
                    />
                    <button onClick={loadTenants}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button onClick={() => navigate("/admin/clients/new")}
                        className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">
                        <Plus className="w-4 h-4" /> New Client
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                                {["#", "Organization", "Slug", "Plan", "Admin Email", "Billing Renewal", "Status", "Actions"].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No clients found</td></tr>
                            ) : filtered.map((t, i) => (
                                <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                    <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{t.org_name}</td>
                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{t.slug}</td>
                                    <td className="px-4 py-3">
                                        {t.plan ? (
                                            <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full">
                                                {t.plan.name}
                                            </span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{t.admin_email}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{t.billing_renewal_date || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${t.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"}`}>
                                            {t.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => navigate(`/admin/clients/${t.id}`)}
                                            className="px-3 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors">
                                            Manage
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
